import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const BreachResponse: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Breach Response — Sprout Docs</title>
        <meta
          name="description"
          content="How Sprout handles a confirmed security incident affecting personal information: notification chain, POPIA s22 obligations, and what schools should prepare in advance."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/security/breach-response" />
      </Helmet>

      <h1>Breach Response</h1>
      <p className="lead">
        POPIA section 22 requires the responsible party (the school) to
        notify the Information Regulator and affected data subjects when
        there are reasonable grounds to believe personal information has
        been accessed or acquired by an unauthorised person. As operator,
        Sprout's job is to detect, contain, and tell the school fast
        enough that the school can meet that obligation.
      </p>

      <h2>What triggers this process</h2>
      <p>
        Any of the following will start the process on Sprout's side:
      </p>
      <ul>
        <li>A confirmed unauthorised access to a tenant's data.</li>
        <li>
          Loss or exposure of credentials, API keys, or backup archives
          containing personal information.
        </li>
        <li>
          A subprocessor (Supabase, Render, Vercel, AWS S3, Resend,
          PostHog — see <Link to="/legal/subprocessors">Subprocessors</Link>)
          notifying us of an incident affecting Sprout-related data.
        </li>
        <li>
          Any other security event with reasonable grounds to believe
          personal information has been accessed or acquired by an
          unauthorised person.
        </li>
      </ul>

      <h2>What we do</h2>
      <p>
        On confirmation of an incident affecting personal information,
        we notify affected schools <strong>without undue delay</strong>{' '}
        so they can in turn notify guardians and the Information
        Regulator within the timeframes section 22 requires. The exact
        wording in our <Link to="/docs/security/popia">POPIA</Link>{' '}
        privacy notice is the contractual commitment; this page expands
        on the operational chain.
      </p>
      <ol>
        <li>
          <strong>Detect and contain.</strong> Investigate the alert,
          revoke the compromised credentials or path, and stop the
          ongoing access.
        </li>
        <li>
          <strong>Scope.</strong> Identify which tenants and which data
          classes (child PII, observations, media, audit logs) are
          affected.
        </li>
        <li>
          <strong>Notify the school's Information Officer.</strong> For
          each affected school, we notify the designated POPIA
          Information Officer (typically the principal or a delegated
          representative) by email and phone. The notification includes
          the incident description, data classes affected, time window,
          containment measures taken, and the recommended next steps.
        </li>
        <li>
          <strong>Support the school's downstream notifications.</strong>{' '}
          The school remains the responsible party for notifying
          guardians and the Information Regulator. We provide the
          factual record the school's notifications will rely on.
        </li>
        <li>
          <strong>Post-incident review.</strong> Document the root
          cause, the fix, and any controls added to prevent recurrence.
          The summary is shared with affected schools.
        </li>
      </ol>

      <h2>The school's role</h2>
      <p>
        Under POPIA, breach notification is the responsible party's
        obligation, not the operator's. The school decides what to tell
        guardians and when, and the school is the entity that notifies
        the Information Regulator. Sprout supports that decision with
        facts and timeline; we do not notify guardians or the Regulator
        on the school's behalf.
      </p>
      <p>
        We strongly recommend that every school using Sprout prepares
        the following <em>before</em> an incident occurs:
      </p>
      <ul>
        <li>
          A named POPIA Information Officer with current contact
          details on file with us.
        </li>
        <li>
          A guardian-notification template covering: nature of the
          incident, categories of personal information affected,
          mitigation steps the school and Sprout have taken, and a
          contact channel for guardian questions.
        </li>
        <li>
          The school's own internal escalation chain — who needs to be
          informed, in what order, and within how long.
        </li>
      </ul>

      <h2>Information Regulator contact</h2>
      <p>
        South Africa's Information Regulator is the authority for POPIA
        breach reports. Their contact details are published on{' '}
        <a
          href="https://inforegulator.org.za"
          target="_blank"
          rel="noopener noreferrer"
        >
          inforegulator.org.za
        </a>
        . The published email for general queries and complaints is{' '}
        <a href="mailto:inforegulator@justice.gov.za">
          inforegulator@justice.gov.za
        </a>
        . Schools should treat the Regulator's site as the authoritative
        source — these details may change over time.
      </p>

      <h2>Reaching Sprout in an incident</h2>
      <p>
        If you suspect an incident on the Sprout side or have received a
        notification you want to verify, contact us at{' '}
        <a href="mailto:privacy@sprout.education">privacy@sprout.education</a>.
        For ongoing operational issues, use the channels listed on our{' '}
        <Link to="/contact">contact page</Link>.
      </p>

      <h2>Related</h2>
      <p>
        See <Link to="/docs/security/popia">POPIA Compliance</Link> for
        the s22 commitment in context, and{' '}
        <Link to="/legal/subprocessors">Subprocessors</Link> for the list
        of third parties whose incidents could trigger this process.
      </p>
    </>
  );
};

export default BreachResponse;
